import { z } from 'zod';
import { defineEndpoint } from '../contract.ts';
import { GuildRole } from '../discord/base.ts';
import { timestamp } from '../schemas.ts';

// Role links for the admin panel (plan 5C, design §6.2, D25): which Discord roles ITC
// Web assigns, from which source, and whether it may remove them. Every change needs a
// recent login. A link is created off, with removal off; removal is turned on by sending
// the number of removals the admin reviewed (R3).

const RecordId = z.string().regex(/^[a-z0-9]{15}$/);
const DiscordId = z.string().regex(/^[0-9]{1,32}$/);
const LinkParams = z.object({ id: RecordId });

export const RoleLink = z.object({
  id: z.string(),
  roleId: z.string(),
  /** The role's name in the guild; null when Discord can't be asked or the role is gone. */
  roleName: z.string().nullable(),
  /** The guild was read and has no such role. */
  roleMissing: z.boolean(),
  /** The role that lets people in: losing it ends their access. */
  gate: z.boolean(),
  /** `<module>:<name>`, or `group:<id>`. */
  source: z.string(),
  sourceLabel: z.string(),
  enabled: z.boolean(),
  removal: z.boolean(),
  /** The source can only add (ITCBot's `add` mode): removal can't be turned on. */
  addOnly: z.boolean(),
  /** The one-time cleanup past the guard that an admin reviewed, as a number of people (it shrinks as runs stage them), or 0 for none. */
  guardAllowance: z.number().int(),
  note: z.string(),
  /** When the outcome below was first seen. */
  lastRunAt: timestamp.nullable(),
  lastAdded: z.number().int(),
  lastRemoved: z.number().int(),
  lastSkipped: z.number().int(),
  /** Why the last run did less than it would have, or null. */
  blocked: z.string().nullable(),
  version: z.number().int(),
  created: timestamp,
  updated: timestamp,
});
export type RoleLink = z.infer<typeof RoleLink>;

export const RoleLinkOutcome = z.object({
  linkId: z.string(),
  roleId: z.string(),
  source: z.string(),
  added: z.number().int(),
  removed: z.number().int(),
  skipped: z.number().int(),
  protected: z.number().int(),
  waiting: z.number().int(),
  blocked: z.string().nullable(),
  error: z.string().nullable(),
});

export const RoleLinkRun = z.object({
  at: timestamp,
  trigger: z.enum(['job', 'admin']),
  /** Why nothing was tried: no link is enabled, or no worker is available. */
  idle: z.string().nullable(),
  changes: z.number().int(),
  error: z.string().nullable(),
  links: z.array(RoleLinkOutcome),
});
export type RoleLinkRun = z.infer<typeof RoleLinkRun>;

export const RoleLinkList = z.object({
  items: z.array(RoleLink),
  /** The guild's roles could be read, so names are real and missing roles are known. */
  rolesAvailable: z.boolean(),
  /** The last run this server made, or null since it started. */
  lastRun: RoleLinkRun.nullable(),
});
export type RoleLinkList = z.infer<typeof RoleLinkList>;

export const RoleSourceOption = z.object({
  id: z.string(),
  label: z.string(),
  /** The module's title, or `Groups`. */
  group: z.string(),
  /** False for a module that is switched off: a link to it is held until it is back. */
  enabled: z.boolean(),
  /** The source can only add, never remove. */
  addOnly: z.boolean().default(false),
});
export type RoleSourceOption = z.infer<typeof RoleSourceOption>;

export const listRoleLinks = defineEndpoint({
  method: 'GET',
  path: '/admin/role-links',
  permission: 'core.admin.roles',
  response: RoleLinkList,
});

export const listRoleLinkSources = defineEndpoint({
  method: 'GET',
  path: '/admin/role-links/sources',
  permission: 'core.admin.roles',
  response: z.object({ items: z.array(RoleSourceOption) }),
});

/** The guild's roles, as the role picker reads them, for someone who may manage links but not groups. */
export const listRoleLinkRoles = defineEndpoint({
  method: 'GET',
  path: '/admin/role-links/roles',
  permission: 'core.admin.roles',
  response: z.object({ roles: z.array(GuildRole), fetchedAt: z.iso.datetime() }),
});

export const createRoleLink = defineEndpoint({
  method: 'POST',
  path: '/admin/role-links',
  permission: 'core.admin.roles',
  recentLogin: true,
  body: z.object({ roleId: DiscordId, source: z.string().min(1).max(200), note: z.string().max(500).default('') }),
  response: RoleLink,
});

export const updateRoleLink = defineEndpoint({
  method: 'PATCH',
  path: '/admin/role-links/:id',
  permission: 'core.admin.roles',
  recentLogin: true,
  params: LinkParams,
  body: z.object({ expectedVersion: z.number().int(), note: z.string().max(500) }),
  response: RoleLink,
});

export const deleteRoleLink = defineEndpoint({
  method: 'DELETE',
  path: '/admin/role-links/:id',
  permission: 'core.admin.roles',
  recentLogin: true,
  params: LinkParams,
  query: z.object({ expectedVersion: z.coerce.number().int() }),
  response: z.object({ ok: z.literal(true) }),
});

/**
 * Turns a link on or off. An enabled link adds; whether it removes is `removal`. Turning
 * a link off also turns removal off (and clears any cleanup allowance) in the same write,
 * so removal has to be earned again with a new review. Turning it on never turns removal on.
 */
export const setRoleLinkEnabled = defineEndpoint({
  method: 'POST',
  path: '/admin/role-links/:id/enabled',
  permission: 'core.admin.roles',
  recentLogin: true,
  params: LinkParams,
  body: z.object({ expectedVersion: z.number().int(), enabled: z.boolean() }),
  response: RoleLink,
});

export const DryRunPerson = z.object({
  discordId: z.string(),
  /** An ITC Web user's name, else the guild's name for them, else null. */
  name: z.string().nullable(),
});
export type DryRunPerson = z.infer<typeof DryRunPerson>;

export const RoleLinkDryRun = z.object({
  linkId: z.string(),
  roleId: z.string(),
  source: z.string(),
  at: timestamp,
  /** Would gain the role. */
  add: z.array(DryRunPerson),
  /** Would lose it: the number to send to turn removal on. */
  remove: z.array(DryRunPerson),
  /** Would lose it, but are effective Administrators and the role lets people in. */
  protected: z.array(DryRunPerson),
  /** In the source, but not in the guild. */
  skipped: z.number().int(),
  holders: z.number().int(),
  /** Why a run would do less than this, or why this can't be trusted. Removal can't be turned on while it is set, bar the case below. */
  blocked: z.string().nullable(),
  /** `blocked` is the safety limit (R2) and nothing else: the one case where `acceptOverGuard` may be sent. */
  guard: z.boolean(),
});
export type RoleLinkDryRun = z.infer<typeof RoleLinkDryRun>;

/** A read, but it asks the worker for a snapshot of the guild. Changes nothing. */
export const dryRunRoleLink = defineEndpoint({
  method: 'GET',
  path: '/admin/role-links/:id/dry-run',
  permission: 'core.admin.roles',
  params: LinkParams,
  response: RoleLinkDryRun,
});

/**
 * Turns removal on or off. On: `expectedRemovals` is how many people the admin saw
 * would lose the role, compared with a fresh dry run (409 if it differs, or if the dry
 * run is blocked); the role that lets people in also needs `confirmGate`. Off needs neither.
 * If the dry run's only block is the safety limit, `acceptOverGuard: true` with the exact
 * count allows one reviewed cleanup past it: the link stores that count as its allowance.
 * With any other block it is still 409, and so is sending it when nothing is over the limit.
 */
export const setRoleLinkRemoval = defineEndpoint({
  method: 'POST',
  path: '/admin/role-links/:id/removal',
  permission: 'core.admin.roles',
  recentLogin: true,
  params: LinkParams,
  body: z.object({
    expectedVersion: z.number().int(),
    removal: z.boolean(),
    expectedRemovals: z.number().int().min(0).optional(),
    confirmGate: z.boolean().optional(),
    acceptOverGuard: z.boolean().optional(),
  }),
  response: RoleLink,
});

/** Runs every enabled link now. 409 if a run is going. */
export const runRoleLinks = defineEndpoint({
  method: 'POST',
  path: '/admin/role-links/run',
  permission: 'core.admin.roles',
  recentLogin: true,
  rateLimit: { max: 6 },
  response: RoleLinkRun,
});
